import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, MapPin, Loader2 } from "lucide-react";
import { Chapter, Reveal } from "@/components/Reveal";
import { JOBS, INQUIRY_TYPES } from "@/data/content";
import WhatsAppIcon from "@/components/WhatsAppFloat";
import { waLink, WA_DEFAULT_MSG, WA_RESUME_MSG } from "@/utils/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", inquiry_type: INQUIRY_TYPES[0], message: "" };

const inputCls =
  "w-full bg-zinc-900 border border-zinc-800 px-5 py-4 text-white placeholder:text-zinc-600 text-sm focus:outline-none focus:border-primary transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [jobs, setJobs] = useState(JOBS);

  useEffect(() => {
    axios
      .get(`${API}/jobs`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setJobs(r.data);
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. Our team will call you back shortly.");
      setForm(empty);
    } catch {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <Chapter num="Chapter 07" label="Get In Touch" />
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight font-medium text-white max-w-2xl">
            Let's make your business journey hassle-free.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <Reveal delay={0.1}>
            <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6">
              Currently hiring for our clients
            </p>
            <div className="border-t border-white/10" data-testid="jobs-list">
              {jobs.map((j, i) => (
                <button
                  key={j.title}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      inquiry_type: "Job Seeker / Career",
                      message: `I'd like to apply for: ${j.title} (${j.location})`,
                    }))
                  }
                  className="w-full flex items-center justify-between gap-4 py-6 border-b border-white/10 group text-left"
                  data-testid={`job-item-${i}`}
                >
                  <div>
                    <p className="font-heading text-xl md:text-2xl font-medium text-white group-hover:text-primary transition-colors duration-300">
                      {j.title}
                    </p>
                    <p className="mt-1 flex items-center gap-2 text-sm text-zinc-500">
                      <MapPin className="w-3.5 h-3.5" /> {j.location} · {j.type}{j.experience ? ` · ${j.experience}` : ""}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-primary group-hover:translate-x-1 transition-all duration-300 shrink-0" />
                </button>
              ))}
            </div>
            <div className="mt-8 border border-[#25D366]/30 bg-[#25D366]/5 p-6 md:p-8" data-testid="resume-whatsapp-card">
              <p className="font-heading text-xl md:text-2xl font-medium text-white">
                Are you a candidate?
              </p>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                Skip the form — send your resume straight to our recruitment
                team on WhatsApp. We review every single one.
              </p>
              <a
                href={waLink(WA_RESUME_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-3.5 font-semibold text-sm hover:brightness-110 transition"
                data-testid="share-resume-whatsapp-button"
              >
                <WhatsAppIcon className="w-5 h-5" /> Share Resume on WhatsApp
              </a>
            </div>

            <div className="mt-8 border border-white/10" data-testid="office-block">
              <div className="p-6">
                <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">
                  Our Office
                </p>
                <p className="font-heading text-xl font-medium text-white" data-testid="office-address">
                  Indore, Madhya Pradesh, India
                </p>
                <p className="text-sm text-zinc-400 mt-1">
                  Serving 400+ cities · admin@hr99global.com
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <form
              onSubmit={submit}
              className="bg-zinc-950/60 backdrop-blur-xl border border-white/10 p-8 md:p-10 space-y-5"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your Name *"
                  className={inputCls}
                  data-testid="contact-name-input"
                />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Email *"
                  className={inputCls}
                  data-testid="contact-email-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input
                  required
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Phone Number *"
                  className={inputCls}
                  data-testid="contact-phone-input"
                />
                <select
                  value={form.inquiry_type}
                  onChange={set("inquiry_type")}
                  className={`${inputCls} appearance-none`}
                  data-testid="contact-inquiry-select"
                >
                  {INQUIRY_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-zinc-900">
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us what you need... *"
                className={`${inputCls} resize-none`}
                data-testid="contact-message-input"
              />
              <button
                type="submit"
                disabled={sending}
                className="group w-full inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 font-semibold hover:bg-orange-400 transition-colors duration-300 disabled:opacity-60"
                data-testid="contact-submit-button"
              >
                {sending ? (
                  <>
                    Sending <Loader2 className="w-5 h-5 animate-spin" />
                  </>
                ) : (
                  <>
                    Send Enquiry
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p className="text-xs text-zinc-600 text-center">
                Prefer chat?{" "}
                <a
                  href={waLink(WA_DEFAULT_MSG)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#25D366] font-semibold hover:underline"
                  data-testid="contact-whatsapp-link"
                >
                  WhatsApp us
                </a>{" "}
                anytime — we respond the same day.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
